import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Paper,
  IconButton,
  Fade,
  Chip,
} from '@mui/material';
import {
  Login,
  Security,
  Speed,
  CloudDone,
  People,
  ArrowForward,
  ArrowBack,
  Phone,
  Email,
  LocationOn,
  MedicalServices,
  Healing,
  FavoriteRounded,
  HealthAndSafety,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  // Hero slider content with local hospital imagery
  const heroSlides = [
    {
      title: "SCULPTE by Plasticare",
      subtitle: "Centre for Cosmetic, Reconstructive and Burns Care",
      description: "Advanced plastic surgery techniques meet compassionate care. Experience excellence in cosmetic enhancement and reconstructive procedures.",
      image: "/images/hero-background.jpg",
      gradient: "linear-gradient(135deg, rgba(44, 122, 156, 0.45) 0%, rgba(74, 155, 189, 0.35) 100%)"
    },
    {
      title: "Hair Transplant Excellence",
      subtitle: "Advanced Hair Restoration Techniques",
      description: "State-of-the-art FUE and FUT hair transplant procedures with natural-looking results. Restore your confidence with our expert team.",
      image: "/images/hair-transplant.jpg",
      gradient: "linear-gradient(135deg, rgba(30, 90, 115, 0.48) 0%, rgba(44, 122, 156, 0.35) 100%)"
    },
    {
      title: "Reconstructive Surgery",
      subtitle: "Restoring Function and Form",
      description: "Expert reconstructive procedures for trauma, cancer, and congenital conditions. Advanced microsurgery techniques for optimal outcomes.",
      image: "/images/reconstructive-surgery.jpg",
      gradient: "linear-gradient(135deg, rgba(19, 65, 82, 0.48) 0%, rgba(44, 122, 156, 0.35) 100%)"
    }
  ];

  // Services data with local images
  const services = [
    {
      title: 'Cosmetic Surgery',
      description: 'Aesthetic procedures to enhance your natural beauty with precision and care',
      icon: <FavoriteRounded sx={{ fontSize: 40 }} />,
      image: '/images/cosmetic-surgery.jpg',
      color: '#2C7A9C'
    },
    {
      title: 'Reconstructive Surgery',
      description: 'Restoring form and function after trauma, disease, or congenital conditions',
      icon: <Healing sx={{ fontSize: 40 }} />,
      image: '/images/reconstructive-surgery.jpg',
      color: '#1E5A73'
    },
    {
      title: 'Burns Care',
      description: 'Specialized treatment and rehabilitation for burn injuries with expert care',
      icon: <HealthAndSafety sx={{ fontSize: 40 }} />,
      image: '/images/hair-transplant.jpg',
      color: '#4A9BBD'
    },
    {
      title: 'Emergency Care',
      description: '24/7 emergency plastic surgery and trauma care with rapid response',
      icon: <MedicalServices sx={{ fontSize: 40 }} />,
      image: '/images/hero-background.jpg',
      color: '#367D9A'
    },
  ];

  const features = [
    {
      title: 'Patient Management',
      description: 'Comprehensive patient records and history management with advanced analytics',
      icon: <People sx={{ fontSize: 48 }} />,
      stats: '10K+ Patients',
      color: '#2C7A9C'
    },
    {
      title: 'Secure & Reliable',
      description: 'Advanced security measures with HIPAA compliance to protect sensitive data',
      icon: <Security sx={{ fontSize: 48 }} />,
      stats: '99.9% Uptime',
      color: '#1E5A73'
    },
    {
      title: 'Fast & Efficient',
      description: 'Streamlined workflows with AI assistance for better productivity',
      icon: <Speed sx={{ fontSize: 48 }} />,
      stats: '50% Faster',
      color: '#4A9BBD'
    },
    {
      title: 'Cloud-Based',
      description: 'Access your data anywhere, anytime with enterprise-grade cloud technology',
      icon: <CloudDone sx={{ fontSize: 48 }} />,
      stats: '24/7 Access',
      color: '#367D9A'
    },
  ];

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        setFadeIn(true);
      }, 300);
    }, 6000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      setFadeIn(true);
    }, 300);
  };

  const prevSlide = () => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
      setFadeIn(true);
    }, 300);
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh', 
        overflow: 'hidden',
        background: '#ffffff',
      }}
    >
      {/* Hero Section with Image Slider */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          minHeight: '600px',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url('${heroSlides[currentSlide].image}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'opacity 0.5s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: heroSlides[currentSlide].gradient,
            }
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Fade in={fadeIn} timeout={800}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8} lg={7}>
                <Stack spacing={3}>
                  {/* Logo and Badge */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box
                      component="img"
                      src="/images/plasticare-logo.png"
                      alt="Plasticare Logo"
                      sx={{
                        width: 70,
                        height: 70,
                        objectFit: 'contain',
                        backgroundColor: 'rgba(255,255,255,0.95)',
                        borderRadius: '50%',
                        padding: 1,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                      }}
                    />
                    <Chip 
                      label="Medical Excellence Since 2022"
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.3)',
                        fontWeight: 500,
                      }}
                    />
                  </Box>
                  
                  {/* Main Title */}
                  <Box>
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                        color: 'white',
                        textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                        lineHeight: 1.1,
                        mb: 1,
                      }}
                    >
                      <Box component="span" sx={{ color: '#8B4513' }}>SCULPTE</Box>
                      <Box component="span" sx={{ fontSize: '0.5em', fontWeight: 400, mx: 1 }}>by</Box>
                      <Box component="span">Plasticare</Box>
                    </Typography>
                    
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 500,
                        color: 'rgba(255,255,255,0.95)',
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        textShadow: '0 1px 4px rgba(0,0,0,0.2)',
                      }}
                    >
                      {heroSlides[currentSlide].subtitle}
                    </Typography>
                  </Box>
                  
                  {/* Description */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      color: 'rgba(255,255,255,0.9)',
                      lineHeight: 1.8,
                      maxWidth: '550px',
                    }}
                  >
                    {heroSlides[currentSlide].description}
                  </Typography>
                  
                  {/* CTA Buttons */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 2 }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<Login />}
                      onClick={() => navigate('/login')}
                      sx={{
                        py: 1.75,
                        px: 4,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        backgroundColor: '#2C7A9C',
                        color: 'white',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: '#1E5A73',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Access Portal
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      endIcon={<ArrowForward />}
                      sx={{
                        py: 1.75,
                        px: 4,
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderRadius: 3,
                        borderColor: 'rgba(255,255,255,0.6)',
                        borderWidth: 2,
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          borderWidth: 2,
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Learn More
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Fade>
        </Container>

        {/* Slider Navigation */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 40,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            zIndex: 3,
          }}
        >
          <IconButton
            onClick={prevSlide}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            <ArrowBack />
          </IconButton>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {heroSlides.map((_, index) => (
              <Box
                key={index}
                onClick={() => {
                  setFadeIn(false);
                  setTimeout(() => {
                    setCurrentSlide(index);
                    setFadeIn(true);
                  }, 300);
                }}
                sx={{
                  width: currentSlide === index ? 32 : 10,
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: currentSlide === index ? 'white' : 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.8)',
                  },
                }}
              />
            ))}
          </Box>
          
          <IconButton
            onClick={nextSlide}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            <ArrowForward />
          </IconButton>
        </Box>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f8fcfd' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#2C7A9C',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block',
              }}
            >
              OUR SERVICES
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: '#1a3a3a',
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}
            >
              Comprehensive Medical Care
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#4a6a6a',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              Expert care across all aspects of plastic surgery, reconstruction, and emergency medicine
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    border: '1px solid #e0f2f1',
                    boxShadow: '0 4px 20px rgba(44, 122, 156, 0.08)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(44, 122, 156, 0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: 180,
                      backgroundImage: `linear-gradient(to bottom, rgba(44, 122, 156, 0.75), rgba(30, 90, 115, 0.9)), url('${service.image}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    {service.icon}
                  </Box>
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{ 
                        mb: 1.5, 
                        fontWeight: 600, 
                        color: '#1a3a3a',
                        fontSize: '1.1rem',
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#4a6a6a', lineHeight: 1.7 }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#f8fffe' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: '#2C7A9C',
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: 'block',
              }}
            >
              WHY CHOOSE US
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                color: '#1a3a3a',
                mb: 2,
                fontSize: { xs: '2rem', md: '2.75rem' },
              }}
            >
              State of the Art Clinic
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#4a6a6a',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              Advanced technology meets exceptional care in our world-class cosmetic and reconstructive surgery centre
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 4,
                    textAlign: 'center',
                    backgroundColor: 'white',
                    border: '1px solid #e0f2f1',
                    borderRadius: 4,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 20px 40px rgba(44,122,156,0.12)',
                      borderColor: '#2C7A9C',
                    },
                  }}
                >
                  <Box 
                    sx={{ 
                      mb: 3,
                      color: feature.color,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{ 
                      mb: 1.5, 
                      fontWeight: 600, 
                      color: '#1a3a3a',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#4a6a6a', lineHeight: 1.7, mb: 2 }}
                  >
                    {feature.description}
                  </Typography>
                  <Chip
                    label={feature.stats}
                    size="small"
                    sx={{
                      backgroundColor: alpha(feature.color, 0.1),
                      color: feature.color,
                      fontWeight: 600,
                    }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box 
        sx={{ 
          py: { xs: 6, md: 10 },
          background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
          color: 'white',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ textAlign: 'center' }}>
            {[
              { value: '15K+', label: 'Patients Treated' },
              { value: '10+', label: 'Cutting Edge Instruments' },
              { value: '100%', label: 'Success Rate' },
              { value:'4.9/5', label: 'Patient Reviews' },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontWeight: 800, 
                    mb: 1,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    color: 'white',
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography 
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    opacity: 0.9,
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                  }}
                >
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact/CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#ffffff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 20px 50px rgba(44, 122, 156, 0.15)',
                }}
              >
                <Box
                  component="img"
                  src="/images/hero-background.jpg"
                  alt="Medical Team"
                  sx={{
                    width: '100%',
                    height: 400,
                    objectFit: 'cover',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h3" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 700,
                  color: '#1a3a3a',
                  fontSize: { xs: '1.75rem', md: '2.25rem' },
                  lineHeight: 1.3,
                }}
              >
                Ready to Transform Your Healthcare Experience?
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  fontSize: '1.1rem', 
                  lineHeight: 1.8, 
                  color: '#4a6a6a',
                }}
              >
                Join thousands of patients who trust SCULPTE by Plasticare for their medical needs. 
                Experience world-class care with cutting-edge technology.
              </Typography>
              
              <Stack spacing={2.5} sx={{ mb: 4 }}>
                {[
                  { icon: <Phone />, text: '072360 59392' },
                  { icon: <Email />, text: 'plasticareaesthetics@gmail.com' },
                  { icon: <LocationOn />, text: 'E 74, Ghatwasan, Kamla Nagar, Agra, Uttar Pradesh 282004' },
                ].map((item, index) => (
                  <Box 
                    key={index}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                    }}
                  >
                    <Box 
                      sx={{ 
                        color: '#2C7A9C',
                        display: 'flex',
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography sx={{ color: '#1a3a3a' }}>
                      {item.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/login')}
                sx={{
                  py: 2,
                  px: 5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #2C7A9C 0%, #4A9BBD 100%)',
                  boxShadow: '0 8px 30px rgba(44,122,156,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E5A73 0%, #2C7A9C 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(44,122,156,0.4)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Get Started Today
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        sx={{ 
          py: 4, 
          backgroundColor: '#1a3a3a',
          color: 'rgba(255,255,255,0.7)',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2">
            © 2025 SCULPTE by Plasticare. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
